import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import api from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { setPermissionsAtom, type PermissionsPayload, type RoleName } from '@/stores/permissions.store';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { captureException } from '@/lib/sentry/sentry-utils';
import { TAGS } from '@/constants/sentry-constants';

interface PermissionsApiResponse {
	statusCode: number;
	success: boolean;
	message: string;
	data: {
		role: RoleName | null;
		roleId: number | null;
		isSuperAdmin: boolean;
		permissions: string[];
	};
}

/**
 * Mount once inside each authenticated layout. Refetches when the active
 * account changes. Account switch in the app does a full reload, so this
 * effect re-runs naturally on the new account.
 */
export function usePermissionsFetcher(): { isFetching: boolean; error: Error | null } {
	const { isAuthenticated, currentUser } = useAuth();
	const setPerms = useSetAtom(setPermissionsAtom);
	const [isFetching, setIsFetching] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const accountId = currentUser?.accountId;

	useEffect(() => {
		if (!isAuthenticated || !accountId) return;

		let cancelled = false;
		setIsFetching(true);
		setError(null);

		api
			.get<PermissionsApiResponse>(API_ENDPOINTS.ME.PERMISSIONS)
			.then((res) => {
				if (cancelled) return;
				const data = res.data.data;
				const payload: PermissionsPayload = {
					role: data.role ?? null,
					roleId: data.roleId ?? null,
					isSuperAdmin: !!data.isSuperAdmin,
					permissions: Array.isArray(data.permissions) ? data.permissions : [],
					accountId,
					fetchedAt: Date.now(),
				};
				setPerms(payload);
			})
			.catch((err: Error) => {
				if (cancelled) return;
				setError(err);
				captureException(err, {
					tags: { [TAGS.ERROR_TYPE]: 'rbac_permissions_fetch' },
					extra: { accountId },
				});
			})
			.finally(() => {
				if (!cancelled) setIsFetching(false);
			});

		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, accountId, setPerms]);

	return { isFetching, error };
}
